'use client';

import Input from '../../../atoms/Input/Input';
import { validationPatterns, getValidationResponse, hasValue } from '../utils';
import type { InputField } from '../types';
import type { UseFormRegister, FieldValues, FieldErrors } from 'react-hook-form';

export type InputControlProps = {
  field: InputField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors<FieldValues>;
  watchedValues?: FieldValues;
};

/**
 * Form control for text, email, and number input fields
 * @component
 * @param {InputControlProps} props - Component props
 * @returns {JSX.Element} Input control component
 */
const InputControl = ({
  field,
  theme,
  direction,
  register,
  errors,
  watchedValues,
}: InputControlProps) => {
  const isDisabledByDependency =
    field.disabledDependency && watchedValues
      ? hasValue(watchedValues[field.disabledDependency.name])
      : false;

  const isDisabled = field.disabled || isDisabledByDependency;

  return (
    <Input
      theme={theme}
      key={`form_field_${field.name}`}
      type={field.type}
      name={field.name}
      label={field.label}
      helperText={field.helperText}
      placeholder={field.placeholder}
      required={field.required}
      disabled={isDisabled}
      onChange={field.onChange}
      allowMultipleFiles={!!field.allowMultipleFiles}
      controlled={!!field.controlled}
      register={() =>
        register(field.name, {
          required: field.required ? field?.validationMessages?.required : false,
          pattern: {
            value: validationPatterns[field?.name] || field.pattern || /.*/,
            message: field?.validationMessages?.pattern || '',
          },
          validate: field?.validate || undefined,
        })
      }
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
    />
  );
};

export default InputControl;
